import {Box, Button, Card, CircularProgress, Stack, Typography} from "@mui/joy";
import {newHousehold} from "@/features/households/schema.js";
import {useCreateHouseholdMutation} from "@/features/households/api.js";
import {useNavigate} from "react-router-dom";
import {useAcceptInvitationMutation, useGetMyInvitationsQuery} from "@/features/users/api.js";
import {OnboardingLayout} from "@/features/onboarding/layouts/OnboardingLayout.jsx";
import {useGetProfileQuery} from "@/features/onboarding/api.js";
import {HouseholdForm} from "@/features/households/components/HouseholdForm.jsx";

export function CreateHouseholdPage() {
  const { data: myInvitations, isLoading: invitationsLoading } = useGetMyInvitationsQuery();
  const { data: profile, isLoading: profileLoading } = useGetProfileQuery();

  const isLoading = invitationsLoading || profileLoading;
  
  return (
    <OnboardingLayout 
      title="Plan together"
      subtitle="Cooperate with your family and plan your meals together."
    >
      {isLoading ?
        <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> :
        <CreateHouseholdForm invitations={myInvitations.data} profile={profile.data} />
      }
    </OnboardingLayout>
  );
}

function CreateHouseholdForm({ invitations, profile }) {  
  const navigate = useNavigate();
  
  const [createHousehold, createResult] = useCreateHouseholdMutation();
  const [acceptInvitation] = useAcceptInvitationMutation();

  const data = newHousehold();
  if (profile) {
    data.name = `${profile.profile.display_name}'s family`;
  }

  async function acceptInvitations() {
    for (const invitation of invitations) {
      await acceptInvitation({ id: invitation.id });
    }
    navigate(`/h/${invitations[0].household_id}`);
  }

  async function save(data) {    
    const result = await createHousehold({ data });
    if (result.data?.data?.id) {
      navigate(`/h/${result.data.data.id}`);
    }    
  }

  return (
    <Stack gap={5}>
      <Stack gap={1}>
        <Typography component="h1" level="h2">{invitations.length > 0 ? "Household invitation" : "Create your household"}</Typography>
        <Typography level="body-md">
          {invitations.length > 0 
            ? "You have been invited to join a household. Accept the invitation to continue."
            : "Set up your household to start planning meals and managing groceries together."}
        </Typography>
      </Stack>
      {invitations.length > 0 ? (
        <Stack gap={2}>
          {invitations.map((invitation) => (
            <Card key={invitation.id} size="sm">
              <Typography level="body-md">{invitation.household_name}</Typography>
            </Card>
          ))}
          <Button 
            loading={createResult.isLoading} 
            onClick={acceptInvitations} 
            size="lg" 
            fullWidth
          >
            Accept Invitation
          </Button>
        </Stack>
      ) : (
        <HouseholdForm data={data} save={save} saveResult={createResult} saveButton={{ label: "Create Household", size: "lg", fullWidth: true, mt: 5 }} />
      )}               
    </Stack>
  );
}
